export type ChatTone = "friendly" | "straightforward" | "professional";
export type ChatResponseLength = "concise" | "balanced" | "detailed";

export const DEFAULT_CHAT_TONE: ChatTone = "straightforward";
export const DEFAULT_CHAT_RESPONSE_LENGTH: ChatResponseLength = "balanced";
export const CUSTOM_INSTRUCTIONS_MAX_WORDS = 1000;

/** URL query key for the non-English natural-tone experiment on agentic chat (client request URL and Express `req.query`). */
export const CHAT_NATURAL_TONE_EXPERIMENT_QUERY_PARAM = "chat_natural_tone_exp";

export const TONE_OPTIONS: Array<{ value: ChatTone; label: string; description: string }> = [
  { value: "friendly", label: "Friendly", description: "Warm and casual" },
  { value: "straightforward", label: "Straightforward", description: "Clear and to the point" },
  { value: "professional", label: "Professional", description: "Formal and direct" },
];

export const RESPONSE_LENGTH_OPTIONS: Array<{ value: ChatResponseLength; label: string; description: string }> = [
  { value: "concise", label: "Concise", description: "Short and focused" },
  { value: "balanced", label: "Balanced", description: "Some additional explanation" },
  { value: "detailed", label: "Detailed", description: "Longer, more thorough answers" },
];

/** Stable prefix for durable reply-language prefs stored as chat personalization memories (must match agent prompts / tooling). */
export const RESPONSE_LANGUAGE_MEMORY_PREFIX = "[agentic_pref] response_language=";

export function normalizeLanguageCode(languageCode: string): string {
  return languageCode.trim().toLowerCase();
}

export function buildResponseLanguagePreferenceMemory(languageCode: string): string {
  return `${RESPONSE_LANGUAGE_MEMORY_PREFIX}${normalizeLanguageCode(languageCode)}`;
}

export function parseResponseLanguagePreferenceMemory(content: string): string | null {
  const normalized = content.trim().toLowerCase();
  if (!normalized.startsWith(RESPONSE_LANGUAGE_MEMORY_PREFIX)) return null;
  const code = normalizeLanguageCode(normalized.slice(RESPONSE_LANGUAGE_MEMORY_PREFIX.length));
  return code.length > 0 ? code : null;
}

/** True when Advanced personalization has saved memories or custom instructions. */
export function hasAdvancedPersonalizationContent(options: {
  memoriesCount: number;
  customInstructions: string;
}): boolean {
  return options.memoriesCount > 0 || options.customInstructions.trim().length > 0;
}
